import {mergeMap, map} from 'rxjs/operators';
import {Inject, Injectable} from '@angular/core';
import {ProjectId, DeviceSlotId, UserId, DeviceSlotReservationId} from '../../types/ids';
import {Observable, ReplaySubject} from 'rxjs';
import {
    isUUID, isNoUUID
} from '../../utils/utils';

import * as _ from 'lodash';
import {DeviceSlot} from '../../types/devices/device-slot';
import {EventsService, WebmateWebsocketEvent} from '../events/events.service';
import {UserService} from '../user.service';
import {DeviceSlotReservation, deviceSlotReservationFromBackendObject} from '../../types/devices/device-slot-reservation';
import {GenericApiResponse} from '../../api/apibase.api';
import {DeviceApi} from '../../api/rest/device.restapi';



/** Repository managing Reservations for Device Slots. */
@Injectable({
    providedIn: 'root'
})
export class DeviceSlotReservationService {

    //  _____ _   _ _______ ______ _____  _   _          _
    // |_   _| \ | |__   __|  ____|  __ \| \ | |   /\   | |
    //   | | |  \| |  | |  | |__  | |__) |  \| |  /  \  | |
    //   | | | . ` |  | |  |  __| |  _  /| . ` | / /\ \ | |
    //  _| |_| |\  |  | |  | |____| | \ \| |\  |/ ____ \| |____
    // |_____|_| \_|  |_|  |______|_|  \_\_| \_/_/    \_\______|


    private _reservationsBySlot: { [s: string /* SlotId*/]: ReplaySubject<DeviceSlotReservation[]> } = {};
    private _slotEventsHandleByProject: { [s: string /* ProjectId*/]: Observable<WebmateWebsocketEvent> } = {}; //events

    constructor(private deviceApi: DeviceApi, private userService: UserService, private eventsService: EventsService) {
    }


    private _registerEventsForProject(projectId: ProjectId): void {
        if (isUUID(projectId) && this._slotEventsHandleByProject[projectId] === undefined) {
            this._slotEventsHandleByProject[projectId] = this.eventsService.getEventsForProject(projectId); //register events!
            this._slotEventsHandleByProject[projectId].subscribe(
                next => DeviceSlotReservationService._handleEvents(next, this, projectId),
                err => console.warn('DeviceSlotReservationService - Events - should never happen!', err /* should never happen, WebSocketService should protect us! */),
                () => DeviceSlotReservationService._handleNoMoreEvents(this, projectId)
            );
        }
    }

    /** the central event handler, makes changes (aka produces new NEXT values) in your Caches depending on the type of Event */
    private static _handleEvents(e: WebmateWebsocketEvent, instanceOfThis: any, forProject: ProjectId): void {
        if (e === undefined) return;

        if (e.eventType === 'DeviceSlot.Updated') {
            if (e.deviceSlotId !== undefined) {
                instanceOfThis._getReservationsForSlotHelper(e.deviceSlotId, forProject).subscribe(); //re-query the Reservations
            }
        }

        /* else, we do not need to do anything. */
    }

    /** function that is called when the Events-Service signals that there will be no more DeviceSlot-Events */
    private static _handleNoMoreEvents(instanceOfThis: any, forProject: ProjectId): void {
        console.warn('DeviceSlotReservationService cleaning Cache since Events-Connection was terminated');
        instanceOfThis.eventsHandle[forProject] = undefined;
        instanceOfThis._cleanCaches();
    }

    private _obtainReservForSlotsFromBackend(slotId: DeviceSlotId): Observable<DeviceSlotReservation[]> {
        return this.deviceApi.getReservationsForSlot(slotId).pipe(map(reservArr => {
            this._reservationsBySlot[slotId].next(reservArr);
            return reservArr;
        }));
    }

    private _getReservationsForSlotHelper(slotId: DeviceSlotId, projectId: ProjectId): Observable<DeviceSlotReservation[]> {
        if (this._reservationsBySlot[slotId] === undefined) this._reservationsBySlot[slotId] = new ReplaySubject(1);
        this._registerEventsForProject(projectId); //we need to listen to events!
        return this._obtainReservForSlotsFromBackend(slotId);
    }


    //   _____ ______ _______      _______ _____ ______             _____ _____
    //  / ____|  ____|  __ \ \    / /_   _/ ____|  ____|      /\   |  __ \_   _|
    // | (___ | |__  | |__) \ \  / /  | || |    | |__        /  \  | |__) || |
    //  \___ \|  __| |  _  / \ \/ /   | || |    |  __|      / /\ \ |  ___/ | |
    //  ____) | |____| | \ \  \  /   _| || |____| |____    / ____ \| |    _| |_
    // |_____/|______|_|  \_\  \/   |_____\_____|______|  /_/    \_\_|   |_____|


    getReservationsForSlot(slotId: DeviceSlotId, projectId: ProjectId): Observable<DeviceSlotReservation[]> {
        if (isNoUUID(slotId)) throw new ReferenceError('\'getReservationsForSlot\' called with an illegal ID: \'' + slotId + '\'');
        if (this._reservationsBySlot[slotId] !== undefined) {
            return this._reservationsBySlot[slotId].asObservable();
        }
        return this._getReservationsForSlotHelper(slotId, projectId).pipe(mergeMap(afterwards => this._reservationsBySlot[slotId].asObservable()))
    }

    /** returns the given Reservation for the specified Slot, will return 'undefined' if there is no such reservation */
    getReservationForSlot(reservationId: DeviceSlotReservationId, slotId: DeviceSlotId, projectId: ProjectId): Observable<DeviceSlotReservation> {
        if (isNoUUID(reservationId)) throw new ReferenceError('\'getReservationForSlot\' called with an illegal ID: \'' + reservationId + '\'');
        return this.getReservationsForSlot(slotId, projectId).pipe(map(reservList => reservList.find(r => r!.id == reservationId)));
    }

    addNewAbsoluteReservationToSlot(slotId: DeviceSlotId, userId: UserId, projectId: ProjectId, begin: string, end: string): Observable<GenericApiResponse> {
        if (isNoUUID(slotId) || isNoUUID(userId)) throw new ReferenceError('\'addNewAbsoluteReservationToSlot\' called with an illegal ID: \'' + slotId + '\'');
        return this.deviceApi.addNewAbsoluteReservationToSlot(slotId, userId, projectId, begin, end);
    }

    removeReservationFromSlot(reservationId: DeviceSlotReservationId, slotId: DeviceSlotId): Observable<any> {
        if (isNoUUID(slotId)) throw new ReferenceError('\'removeReservationFromSlot\' called with an illegal ID: \'' + slotId + '\'');
        return this.deviceApi.removeReservationFromSlot(reservationId, slotId);
    }
}
